import { RefObject, useEffect, useRef } from 'react';

// Calls `onClickOutside` when the user presses the mouse anywhere outside the
// elements in `insideRefs`. Used to close menus and popups.
//
// `insideRefs` must include the button that opens and closes the popup, not
// just the popup itself. Otherwise pressing that button to close the popup
// closes it on mousedown, and the button's own click handler then reopens it.
//
// Listens only while `active`, so a closed popup adds no document listener.
export function useClickOutside(
  insideRefs: ReadonlyArray<RefObject<HTMLElement | null>>,
  onClickOutside: () => void,
  active: boolean
): void {
  // Read at click time, so the listener isn't re-added every time the caller
  // re-renders with a new array or callback.
  const latest = useRef({ insideRefs, onClickOutside });
  useEffect(() => {
    latest.current = { insideRefs, onClickOutside };
  });

  useEffect(() => {
    if (!active) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const { insideRefs: refs, onClickOutside: onOutside } = latest.current;
      if (refs.some((ref) => ref.current?.contains(target))) return;
      onOutside();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [active]);
}
